import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Check, Clock, MessageCircle } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { MINI_COURSES } from "@/data/content";
import { useBooking } from "@/App";
import { waLink } from "@/config";

const DetailSection = ({ num, title, children }) => (
  <div className="py-7 border-b border-botanical/10 last:border-0">
    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-2">{num}</p>
    <h3 className="font-serif text-2xl text-forest mb-4">{title}</h3>
    {children}
  </div>
);

const CheckList = ({ items, iconColor = "text-sage", testidPrefix }) => (
  <ul className="space-y-2.5">
    {items.map((item, i) => (
      <li
        key={i}
        data-testid={testidPrefix ? `${testidPrefix}-${i}` : undefined}
        className="flex items-start gap-3 text-sm text-inkmuted leading-relaxed"
      >
        <Check size={16} className={`${iconColor} mt-0.5 shrink-0`} />
        {item}
      </li>
    ))}
  </ul>
);

function CourseDetail({ course, onClose }) {
  const { openBooking } = useBooking();

  useEffect(() => {
    if (course) {
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      window.__lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [course]);

  return (
    <AnimatePresence>
      {course && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-forest/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={`${course.name} details`}
          data-testid="course-detail-modal"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-ivory w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[88vh] sm:rounded-3xl rounded-t-3xl overflow-y-auto shadow-hover"
          >
            <div className="relative h-56 sm:h-64 shrink-0">
              <img src={course.image} alt={`${course.name} — ${course.category}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ivory via-forest/30 to-forest/20" />
              <button
                onClick={onClose}
                data-testid="course-detail-close-button"
                aria-label="Close course details"
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ivory/90 flex items-center justify-center text-forest hover:bg-ivory transition-colors"
              >
                <X size={20} />
              </button>
              <div className="absolute bottom-5 left-6 sm:left-10 right-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
                  {course.category} • {course.duration}
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl text-forest mt-1">{course.name}</h2>
              </div>
            </div>

            <div className="px-6 sm:px-10 pb-10">
              <p className="font-serif italic text-xl text-botanical pt-7">"{course.tagline}"</p>

              <DetailSection num="01" title="Who This Is For">
                <CheckList items={course.whoFor} testidPrefix="course-whofor" />
              </DetailSection>

              <DetailSection num="02" title="What You Will Learn">
                <CheckList items={course.learn} iconColor="text-gold" testidPrefix="course-learn" />
              </DetailSection>

              <DetailSection num="03" title="What Is Included">
                <div className="grid sm:grid-cols-2 gap-x-6">
                  <CheckList items={course.included} testidPrefix="course-included" />
                </div>
              </DetailSection>

              <DetailSection num="04" title="How It Works">
                <ol className="space-y-3">
                  {course.howItWorks.map((s, i) => (
                    <li key={i} className="flex items-start gap-4 text-sm text-inkmuted leading-relaxed">
                      <span className="font-serif italic text-xl text-gold shrink-0 w-7">{i + 1}.</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </DetailSection>

              <div className="pt-8">
                <p className="text-xs text-inkmuted/80 leading-relaxed italic mb-6">{course.disclaimer}</p>
                {course.cta === "join" ? (
                  <div className="space-y-3">
                    <motion.a
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      href={waLink(
                        "Hi Dr. Anjali, I visited the Aarogyam Bharat website and I would like to join the 21-Day Healthy Habits Challenge™. Please share the next steps."
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="course-detail-join-button"
                      className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] text-forest px-8 py-4 text-sm font-extrabold tracking-wide hover:brightness-95 transition-all"
                    >
                      <MessageCircle size={17} /> JOIN THE CHALLENGE
                    </motion.a>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        onClose();
                        openBooking();
                      }}
                      data-testid="course-detail-book-button"
                      className="w-full inline-flex items-center justify-center gap-2.5 rounded-full border border-forest/25 text-forest px-8 py-4 text-sm font-bold tracking-wide hover:border-forest hover:bg-forest/5 transition-colors"
                    >
                      BOOK YOUR FREE 1:1 DISCOVERY CALL <ArrowRight size={16} />
                    </motion.button>
                  </div>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      onClose();
                      openBooking();
                    }}
                    data-testid="course-detail-book-button"
                    className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-forest text-ivory px-8 py-4 text-sm font-bold tracking-wide hover:bg-botanical transition-colors"
                  >
                    BOOK YOUR FREE 1:1 DISCOVERY CALL <ArrowRight size={16} />
                  </motion.button>
                )}
                <p className="text-center text-xs text-inkmuted mt-4">
                  Mini courses complement personalised coaching — your Discovery Call helps you choose the right
                  starting point.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const PICKER = [
  { q: "Want to improve your daily metabolic habits?", a: "Start with the 7-Day Metabolic Reset." },
  { q: "Want to focus on digestion and mindful eating?", a: "Start with the 5-Day Gut Reset." },
  { q: "Want a gentle lifestyle refresh?", a: "Start with the 5-Day Detox Reset." },
  { q: "Want to improve your sleep routine?", a: "Try Sleep Restore." },
  { q: "Feeling mentally overloaded?", a: "Start with the 5-Day Stress Reset." },
  { q: "Want to build consistency?", a: "Join the 21-Day Healthy Habits Challenge." },
];

export default function MiniCourses() {
  const [active, setActive] = useState(null);
  const { openBooking } = useBooking();

  return (
    <section id="mini-courses" className="py-24 sm:py-32 bg-sagesurface/50" data-testid="mini-courses-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Mini Courses & Health Challenges"
          title="Small Steps. Focused Guidance. Real-Life Health Habits."
          sub="Not ready for personalised 1:1 coaching yet? Start with a focused mini course or challenge designed to help you take one practical step at a time."
        />

        <div className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory no-scrollbar lg:grid lg:grid-cols-3 lg:overflow-visible lg:pb-0">
          {MINI_COURSES.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.06} className="min-w-[85%] sm:min-w-[60%] lg:min-w-0 snap-center">
              <motion.article
                whileHover={{ y: -10 }}
                transition={{ duration: 0.4 }}
                className="h-full bg-ivory border border-botanical/10 rounded-3xl overflow-hidden shadow-card hover:shadow-hover transition-shadow duration-300 flex flex-col"
                data-testid={`course-card-${c.slug}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <motion.img
                    whileHover={{ scale: 1.06 }}
                    transition={{ duration: 0.6 }}
                    src={c.image}
                    alt={`${c.name} — ${c.category}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-forest/10 to-transparent" />
                  <span
                    className="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full text-forest"
                    style={{ backgroundColor: c.accent }}
                  >
                    {c.category}
                  </span>
                  <span className="absolute bottom-3.5 left-4 inline-flex items-center gap-1.5 text-ivory text-xs font-bold tracking-wide">
                    <Clock size={13} /> {c.duration}
                  </span>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-serif text-2xl text-forest leading-tight">{c.name}</h3>
                  <p className="mt-2.5 font-serif italic text-lg text-botanical leading-snug">"{c.tagline}"</p>
                  <p className="mt-3 text-sm leading-relaxed text-inkmuted">{c.description}</p>
                  <div className="mt-5 pt-5 border-t border-botanical/10 flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-3">
                      What you'll explore
                    </p>
                    <CheckList items={c.explore} testidPrefix={`course-explore-${c.slug}`} />
                  </div>
                  <button
                    onClick={() => setActive(c)}
                    data-testid={`course-cta-${c.slug}`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-bold tracking-wide text-forest hover:text-sage transition-colors group self-start"
                  >
                    {c.cta === "join" ? "JOIN THE CHALLENGE" : "EXPLORE COURSE"}
                    <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-16">
          <div className="max-w-3xl mx-auto bg-ivory border border-botanical/10 rounded-3xl px-8 py-10 shadow-card" data-testid="course-picker">
            <h3 className="font-serif text-2xl sm:text-3xl text-forest text-center">
              Which One Should You Start With?
            </h3>
            <p className="mt-3 text-sm text-inkmuted text-center">
              Choose the area you want to work on right now.
            </p>
            <div className="mt-8 space-y-4">
              {PICKER.map((p, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm" data-testid={`course-picker-row-${i}`}>
                  <span className="text-inkmuted">{p.q}</span>
                  <span className="hidden sm:inline text-gold">→</span>
                  <span className="font-bold text-forest">{p.a}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-center font-serif italic text-lg text-botanical">
              Not sure where to begin? You don't have to figure it out alone.
            </p>
            <div className="mt-6 text-center">
              <motion.button
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                onClick={openBooking}
                data-testid="course-picker-book-button"
                className="inline-flex items-center gap-2.5 rounded-full bg-forest text-ivory px-8 py-4 text-sm font-bold tracking-wide shadow-card hover:bg-botanical transition-colors"
              >
                BOOK YOUR FREE 1:1 DISCOVERY CALL <ArrowRight size={16} />
              </motion.button>
            </div>
          </div>
        </Reveal>
      </div>

      <CourseDetail course={active} onClose={() => setActive(null)} />
    </section>
  );
}
